import keyBy from 'lodash/keyBy'

import { AddressManager } from '@app/utils/AddressManager'
import { createSelector } from '@app/utils/createSelector'
import { rectangleCoveredBySet, reduceRectangles } from '@app/utils/geometry'
import { isTruthy } from '@app/utils/isTruthy'
import type { SearchParamsManager } from '@app/utils/search/SearchParamsManager'

import { getAbortSignal } from '@app/packages/abortContext/actions'
import type { MapBounds } from '@app/packages/geo/MapBounds'
import type { Point } from '@app/packages/geo/Point'

import { createReduxSlice } from '@app/store/redux_slice'
import { createThunk } from '@app/store/thunk'

export type SearchMapResult = { id: string; token: string; name: string; coords: Point }

export type SearchMapResultsState = {
  results: Record<string, SearchMapResult>
  area: MapBounds[]
}

const searchMapResultsSlice = createReduxSlice<SearchMapResultsState>('search_map_results')

export const searchMapResultsSelector = createSelector([searchMapResultsSlice.selector], state => state?.results ?? EMPTY_RESULTS)
export const searchMapResultsAreaSelector = createSelector([searchMapResultsSlice.selector], state => state?.area ?? EMPTY_AREA)

export const fetchMapResults = (manager: SearchParamsManager, { initial: initialInitial = false }: { initial?: boolean } = {}) => {
  return createThunk(async (dispatch, getState) => {
    const [{ getSearchUsersLocations }, { callV2Api }] = await Promise.all([import('@app/packages/api/v2/actions/search'), import('@app/store/actions/api')])
    const abortSignal = dispatch(getAbortSignal())
    const params = manager.effectiveParams
    const bounds = params.address ? new AddressManager(params.address).asRectangle() : undefined
    if (!bounds) return
    let page = 1
    // eslint-disable-next-line no-useless-assignment
    let total = 0
    const per_page = 500

    const canSkip = !initialInitial && rectangleCoveredBySet(bounds, searchMapResultsAreaSelector(getState()))

    while (!canSkip) {
      const initial = initialInitial && page === 1
      if (abortSignal?.aborted) return

      const resp = await dispatch(callV2Api(getSearchUsersLocations(await manager.toApiQuery({ page, per_page }))))

      const locations = keyBy(resp.included, 'id')
      const list = resp.data
        .map(u => {
          const locId = u.relationships.inaccurate_location.data?.id
          if (!locId) return null
          const loc = locations[locId]
          if (!loc) return null
          return {
            id: u.id,
            token: u.attributes.token,
            name: u.attributes.name,
            coords: { lat: loc.attributes.rough_latitude, lon: loc.attributes.rough_longitude },
          }
        })
        .filter(isTruthy)

      dispatch(
        searchMapResultsSlice.update(state => {
          const base = state ?? DEFAULT_STATE
          const usersMap = keyBy(list, 'token')
          return {
            results: initial ? usersMap : { ...base.results, ...usersMap },
            area: reduceRectangles([...(initial ? [] : base.area), bounds]),
          }
        })
      )

      total = resp.meta.total_pages
      ++page

      if (page > total) break
    }
  })
}

const DEFAULT_STATE: SearchMapResultsState = { results: {}, area: [] }
const EMPTY_RESULTS: Record<string, SearchMapResult> = {}
const EMPTY_AREA: MapBounds[] = []
